import Flex from "common/Boxes/Flex";
import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import ChangeTag from "common/Tags/ChangeTag";
import { DISPLAY_CURRENCIES } from "common/constants";
import { BTC } from "common/Icons/Icons";
import React, { useMemo } from "react";
import styled, { useTheme } from "styled-components";
import { useSelector } from "react-redux";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { BotSummaryData } from "features/dashboard/common/types";
import useMeasure from "utils/hooks/useMeasure";
import {
  BOTS_SUMMARY_CARD_WIDTH,
  PADDING,
} from "features/dashboard/common/constants";
import { BotType, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface BotsCardStatisticsProps {
  data: BotSummaryData;
}

const BotsCardStatistics = ({ data }: BotsCardStatisticsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.DASHBOARD);
  const displayCurrency = useSelector(selectDisplayCurrency);
  const { isNewLook } = useTheme();
  const [openTradesMeasureRef, { width: openTradesStatisticWidth }] =
    useMeasure<HTMLDivElement>();
  const [closedTradesMeasureRef, { width: closedTradesStatisticWidth }] =
    useMeasure<HTMLDivElement>();

  const shouldUseAutoWidth = (width: number) =>
    width > (BOTS_SUMMARY_CARD_WIDTH - PADDING * 3) / 2;

  const useAutoWidth =
    shouldUseAutoWidth(openTradesStatisticWidth) ||
    shouldUseAutoWidth(closedTradesStatisticWidth);

  const fontSize = useMemo(() => {
    const openTradesStrLength = data?.numOpenTrades?.toString().length || 0;
    const closedTradesStrLength = data?.numClosedTrades?.toString().length || 0;
    if (openTradesStrLength >= 8 || closedTradesStrLength >= 8) return 11;
    else if (openTradesStrLength >= 6 || closedTradesStrLength >= 6) return 12;
    else return 14;
  }, [data]);

  const changeTagProps = {
    hideBackground: isNewLook,
    prefix:
      displayCurrency === DISPLAY_CURRENCIES.USD ? (
        "$"
      ) : (
        <BTC style={{ fontSize: 10 }} />
      ),
    showArrow: false,
    style: isNewLook
      ? { fontSize: 12, marginLeft: 6, fontWeight: 500 }
      : { fontSize: 11, marginLeft: 4 },
  };

  const getTradesStatistic = (isOpen?: boolean) => {
    return (
      <Flex
        $flexDirection="column"
        style={{ width: useAutoWidth ? "auto" : "50%" }}
        ref={isOpen ? openTradesMeasureRef : closedTradesMeasureRef}
      >
        <SecondaryText>
          {isOpen ? t("bots.open_trades") : t("bots.closed_trades")}
        </SecondaryText>
        <Flex>
          <Title
            style={{ fontSize }}
            $weight={isNewLook ? NEW_LOOK_VALUE_WEIGHT : undefined}
          >
            {data[isOpen ? "numOpenTrades" : "numClosedTrades"]}
          </Title>
          <ChangeTag
            value={
              data[isOpen ? "openTradesChange" : "closedTradesChange"]?.[
                displayCurrency
              ] || 0
            }
            {...changeTagProps}
          />
        </Flex>
      </Flex>
    );
  };

  if (data.botType === BotType.GRID) {
    return (
      <Flex $flexDirection="column" style={{ marginBottom: 12 }}>
        <SecondaryText>{t("bots.grid_take_profits_filled")}</SecondaryText>
        <Flex>
          <Title level={4}>{data.takeProfitsFilled}</Title>
          <ChangeTag
            value={data.realisedPnl?.[displayCurrency] || 0}
            {...changeTagProps}
          />
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex style={{ marginBottom: 12 }}>
      {getTradesStatistic(true)}
      <VerticalBorder />
      {getTradesStatistic(false)}
    </Flex>
  );
};

export default BotsCardStatistics;

// New look: the counts are inline values (500), not headings.
const NEW_LOOK_VALUE_WEIGHT = 500;
const SecondaryText = styled(Text).attrs({ type: "secondary" })`
  font-size: ${(p) => p.theme.typography.fontSizes.S};
  text-align: center;
  margin-bottom: 2px;
`;
const VerticalBorder = styled.div`
  border-left: 1px solid ${(p) => p.theme.colors.lightBorderGrey};
  ${(p) => p.theme.isNewLook && `border-left: ${p.theme.borders.lightest};`}
  height: 40px;
  margin-left: 8px;
  padding-right: 8px;
`;
